import { Tooltip } from 'antd'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons'

import { isObject } from '../../../utils'
import { ColorsPalette } from '../../../config/colors'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'

import '../../../assets/entity-badge.scss'

/**
 * Institutions and schools carry the same commercial categories, so they share a single badge.
 * The order of the keys is the display order : it drives both the grouping and the sort rank.
 * @enum {string}
 */
export const EntityCategories = Object.freeze({
  PREMIUM: 'premium',
  MANAGED: 'managed',
  VERIFIED: 'verified',
  OTHERS: 'others'
})

export const CATEGORY_ORDER = Object.freeze(Object.values(EntityCategories))

/**
 * The badge is the solid icon rather than the duotone one : the duotone renders its shape at 40%
 * opacity, which washes the category color out. It matches the premium icon of the school options.
 * OTHERS is the catch-all : it groups the entities that carry no badge at all.
 */
const CATEGORY_COLORS = Object.freeze({
  [EntityCategories.PREMIUM]: ColorsPalette.PREMIUM,
  [EntityCategories.MANAGED]: ColorsPalette.MANAGED,
  [EntityCategories.VERIFIED]: ColorsPalette.VERIFIED
})

export const getEntityCategory = entity => {
  if (!isObject(entity)) {
    return EntityCategories.OTHERS
  }

  if (entity.premium) {
    return EntityCategories.PREMIUM
  }

  if (entity.managed) {
    return EntityCategories.MANAGED
  }

  if (entity.verified) {
    return EntityCategories.VERIFIED
  }

  return EntityCategories.OTHERS
}

export const getEntityRank = entity => CATEGORY_ORDER.indexOf(getEntityCategory(entity))

export const getCategoryTranslationKey = category => `internship.institutionCategories.${category}`

const EntityBadge = ({ entity, size = 'medium', withTooltip = false }) => {
  const t = useSelector(selectTranslate)

  const category = getEntityCategory(entity)

  if (category === EntityCategories.OTHERS) {
    return null
  }

  const badge = <FontAwesomeIcon icon={faBadgeCheck} color={CATEGORY_COLORS[category]} size={size} className='entity-badge' />

  if (!withTooltip) {
    return badge
  }

  return (
    <Tooltip title={t(getCategoryTranslationKey(category))}>
      {badge}
    </Tooltip>
  )
}

export default EntityBadge
